// Boss Fights (ADR-0025) create page. Author a DRAFT boss.

import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../../lib/api';
import { ApiError } from '../../../lib/api';
import { bossesApi } from './api';
import type { JiraEpicSummary } from './api';

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function BossNewPage() {
  const nav = useNavigate();
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: { id: string; name: string }[] }>('/api/teams'),
  });
  const [teamId, setTeamId] = useState('');
  const [name, setName] = useState('');
  const [epicKey, setEpicKey] = useState('');
  const [startsAt, setStartsAt] = useState(toLocalInput(new Date().toISOString()));
  const [endsAt, setEndsAt] = useState(toLocalInput(new Date(Date.now() + 30 * 86400000).toISOString()));

  // Phase 2: import from Jira epic. Lists the team's epics + a
  // preview of the HP pool (children + points).
  const epics = useQuery({
    queryKey: ['jira-epics', teamId],
    queryFn: () => bossesApi.listJiraEpics(teamId),
    enabled: !!teamId,
  });
  const [preview, setPreview] = useState<{ total_points: number | null; hp_pool: { key: string; points: number | null }[] } | null>(null);

  const pickEpic = async (epic: JiraEpicSummary) => {
    setEpicKey(epic.key);
    setName(epic.name);
    const p = await bossesApi.previewEpic(teamId, epic.key);
    setPreview(p);
  };

  // v0.8.9: dropped HP multiplier and damage event-type knobs from
  // the UI. The scoring engine now ignores them (HP = Σ max(points, 1)
  // per epic child, damage = same on issue.transition to a terminal
  // status). The api still accepts them for back-compat with old DB
  // rows but the create/update forms no longer surface them.
  const create = useMutation({
    mutationFn: () => bossesApi.create({
      team_id: teamId,
      name,
      jira_epic_key: epicKey || null,
      starts_at: new Date(startsAt).toISOString(),
      ends_at: new Date(endsAt).toISOString(),
    }),
    onSuccess: (r) => nav(`/admin/bosses/${r.boss.id}`),
  });

  const err = create.error instanceof ApiError ? create.error : null;

  const canSubmit = teamId && name && startsAt && endsAt && new Date(endsAt) > new Date(startsAt);

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <Link to="/admin/bosses" className="text-sm text-ink-muted hover:text-ink">← Back to bosses</Link>
      <h1 className="text-2xl font-semibold text-ink mt-4 mb-6">New boss</h1>
      <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }} className="bg-bg-elev border border-border rounded p-5 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-ink-muted mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Convergence VPN" className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs text-ink-muted mb-1">Team</label>
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)} className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm">
              <option value="">Select team</option>
              {(teams.data?.teams ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs text-ink-muted mb-1">Jira epic key (optional)</label>
          <input value={epicKey} onChange={(e) => setEpicKey(e.target.value)} placeholder="YOLO-6141" className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm font-mono" />
        </div>
        {teamId && (
          <div className="bg-bg-base border border-border rounded p-3">
            <label className="block text-xs text-ink-muted mb-2">Import from Jira epic</label>
            {epics.isPending ? (
              <div className="text-sm text-ink-muted">Loading epics…</div>
            ) : epics.isError ? (
              <div className="text-sm text-amber-400">Couldn't load epics. Check the Jira ingestor + token.</div>
            ) : (epics.data?.epics ?? []).length === 0 ? (
              <div className="text-sm text-ink-muted">No epics found. Enter a key manually.</div>
            ) : (
              <select
                onChange={(e) => {
                  const ep = (epics.data?.epics ?? []).find((x) => x.key === e.target.value);
                  if (ep) void pickEpic(ep);
                }}
                value=""
                className="w-full px-3 py-2 bg-bg-elev border border-border rounded text-sm"
              >
                <option value="">Select an epic…</option>
                {(epics.data?.epics ?? []).map((ep) => (
                  <option key={ep.key} value={ep.key}>
                    {ep.key} — {ep.name} ({ep.issue_count} issues, {ep.total_points ?? '?'} pts)
                  </option>
                ))}
              </select>
            )}
            {preview && (
              <p className="text-xs text-ink-muted mt-2">
                HP pool: {preview.hp_pool.length} tickets, {preview.total_points ?? 0} story points total.
                Unestimated tickets still deal 1 damage each.
              </p>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-ink-muted mb-1">Starts</label>
            <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs text-ink-muted mb-1">Ends</label>
            <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm" />
          </div>
        </div>
        {err && <div className="text-sm text-red-400">Failed to create boss: {err.message}</div>}
        {startsAt && endsAt && new Date(endsAt) <= new Date(startsAt) && (
          <div className="text-sm text-amber-400">Ends must be after starts.</div>
        )}
        <div className="flex justify-end">
          <button type="submit" disabled={!canSubmit || create.isPending} className="px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark disabled:opacity-50">
            {create.isPending ? 'Creating…' : 'Create boss'}
          </button>
        </div>
      </form>
    </div>
  );
}
